import { useServiceWithAddonConfig } from '@/modules/Service/services'
import { Service, ServiceConfigoption } from '@/modules/Service/types'
import {
    SERVICE_CREATING_KEY,
    SERVICE_CREATING_POC_KEY,
} from '@/modules/Ticket/constants'
import { formatNumberUsage } from '@/utils/function'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

export interface PreviewInformationProps {
    service: Service
    ticketTypeKey?: string
}

interface ConfigGroup {
    type: string
    items: (ServiceConfigoption & { isAddon?: boolean; addonName?: string })[]
}

const BaseInformation: React.FC<PreviewInformationProps> = ({
    service,
    ticketTypeKey,
}) => {
    const { t } = useTranslation()
    const { data, isLoading } = useServiceWithAddonConfig(service?.service_id)

    // Check if should show Portal name field - KHÔNG hiển thị cho service_creating & service_creating_poc
    const showPortalName =
        ticketTypeKey !== SERVICE_CREATING_KEY &&
        ticketTypeKey !== SERVICE_CREATING_POC_KEY

    // Group configoptions by type
    const groupedConfigs = useMemo(() => {
        if (!data) return []

        const allConfigs: (ServiceConfigoption & {
            isAddon?: boolean
            addonName?: string
        })[] = [
            ...(data.configoptions || []).map((item) => ({
                ...item,
                isAddon: false,
            })),
            ...(data.addons?.flatMap(
                (addon: Service & { configoptions: ServiceConfigoption[] }) =>
                    (addon.configoptions || []).map((config) => ({
                        ...config,
                        isAddon: true,
                        addonName: addon.service_name,
                    }))
            ) || []),
        ].filter(
            (item) =>
                item.commitment_value !== null &&
                item.commitment_value !== undefined &&
                item.commitment_value > 0
        )

        // Group by type
        const grouped: Record<string, ConfigGroup> = {}
        allConfigs.forEach((config) => {
            const type = config.type || 'other'
            if (!grouped[type]) {
                grouped[type] = { type, items: [] }
            }
            grouped[type].items.push(config)
        })

        return Object.values(grouped)
    }, [data])

    // Check if has addons
    const hasAddons = useMemo(() => {
        return data?.addons && data.addons.length > 0
    }, [data])

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-2">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </div>
        )
    }

    // If no config options, show PAYG mode
    if (groupedConfigs.length === 0) {
        return (
            <div className="bg-blue-50 rounded p-3 border border-blue-100">
                <Typogaphy size="small" className="text-blue-700 font-medium">
                    {t('service-payg-mode')}
                </Typogaphy>
                <Typogaphy size="small" className="text-blue-600 text-xs mt-1">
                    {t('service-payg-description')}
                </Typogaphy>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {/* Portal Name - chỉ hiển thị cho service_creating & service_creating_poc */}
            {showPortalName && (
                <div className="bg-amber-50 rounded p-3 border border-amber-200">
                    <Typogaphy
                        size="small"
                        className="text-amber-700 font-medium mb-1"
                    >
                        Portal Name
                    </Typogaphy>
                    <Typogaphy size="small" className="text-amber-900">
                        {service.name || '---'}
                    </Typogaphy>
                </div>
            )}

            {/* Config options by group */}
            {groupedConfigs.map((group) => (
                <div
                    key={group.type}
                    className="bg-white rounded p-3 border border-slate-100"
                >
                    <Typogaphy
                        size="small"
                        className="text-slate-600 font-medium mb-2 uppercase text-xs"
                    >
                        {group.type.replace(/-/g, ' ')}
                    </Typogaphy>
                    <div className="space-y-1.5">
                        {group.items.map((item, idx) => (
                            <div
                                key={`${item.service_configoption_id}-${idx}`}
                                className="flex items-center justify-between text-xs"
                            >
                                <div className="flex items-center gap-1.5">
                                    <span className="text-slate-700">
                                        {t(item.name)}
                                    </span>

                                    {item.isAddon && (
                                        <span className="px-1 py-0.5 bg-purple-100 text-purple-600 rounded text-[10px]">
                                            Addon
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-0.5">
                                    <span className="text-slate-800 font-medium">
                                        {formatNumberUsage(
                                            item?.commitment_value
                                        )}
                                    </span>
                                    {item.unit && (
                                        <span className="text-slate-400 uppercase">
                                            ({item.unit})
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Addons count */}
            {hasAddons && (
                <div className="text-xs text-slate-500 pt-1">
                    {t('service-has-addons', {
                        count: data?.addons?.length || 0,
                    })}
                </div>
            )}
        </div>
    )
}

export default BaseInformation
